/**
 * Analytics Utilities
 * 
 * This module provides analytics tracking functions for monitoring
 * user progress through the funnel. Can be connected to various
 * analytics platforms (Google Analytics, Mixpanel, etc.)
 */

import { AnalyticsEvent, FunnelState, ServiceType } from '../types/funnel.types';

// ============================================
// TYPE DEFINITIONS
// ============================================

// Declare gtag for Google Analytics integration
declare global {
  interface Window {
    gtag?: (
      command: 'event' | 'config' | 'set',
      action: string,
      params?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
  }
}

// ============================================
// EVENT NAMES
// ============================================

/**
 * Standardized event names for the funnel
 * Use these constants to ensure consistency
 */
export const ANALYTICS_EVENTS = {
  // Funnel progression
  FUNNEL_STARTED: 'funnel_started',
  STEP_VIEWED: 'funnel_step_viewed',
  STEP_COMPLETED: 'funnel_step_completed',
  STEP_SKIPPED: 'funnel_step_skipped',
  FUNNEL_COMPLETED: 'funnel_completed',
  FUNNEL_ABANDONED: 'funnel_abandoned',
  
  // User interactions
  OPTION_SELECTED: 'funnel_option_selected',
  TEXT_INPUT_STARTED: 'funnel_text_input_started',
  BACK_BUTTON_CLICKED: 'funnel_back_clicked',
  
  // Errors
  VALIDATION_ERROR: 'funnel_validation_error',
  SUBMISSION_ERROR: 'funnel_submission_error',
  
  // Page events
  PAGE_LOADED: 'funnel_page_loaded',
  SESSION_RESTORED: 'funnel_session_restored',
} as const;

// ============================================
// TRACKING FUNCTIONS
// ============================================

/**
 * Track an analytics event
 * Sends to Google Analytics if available, logs otherwise
 * 
 * @param eventName - Name of the event to track
 * @param properties - Additional properties for the event
 */
export function trackEvent(
  eventName: string,
  properties: Record<string, unknown> = {}
): void {
  // Create event object with timestamp
  const event: AnalyticsEvent = {
    eventName,
    properties: {
      ...properties,
      // Add common properties
      page_path: window.location.pathname,
      page_url: window.location.href,
    },
    timestamp: new Date().toISOString(),
  };

  // Log in development
  if (import.meta.env.DEV) {
    console.log('📊 Analytics Event:', event);
  }

  // Send to Google Analytics if available
  if (window.gtag) {
    window.gtag('event', eventName, event.properties);
  }

  // You can add other analytics platforms here:
  // - Mixpanel: mixpanel.track(eventName, event.properties)
  // - Amplitude: amplitude.logEvent(eventName, event.properties)
  // - Segment: analytics.track(eventName, event.properties)
}

/**
 * Track when a user views a step
 */
export function trackStepViewed(
  stepNumber: number,
  questionId: string,
  serviceType?: ServiceType
): void {
  trackEvent(ANALYTICS_EVENTS.STEP_VIEWED, {
    step_number: stepNumber,
    question_id: questionId,
    service_type: serviceType || 'not_selected',
  });
}

/**
 * Track when a user completes a step
 */
export function trackStepCompleted(
  stepNumber: number,
  questionId: string,
  answerType: 'text' | 'selection' | 'multi-field',
  timeOnStep?: number
): void {
  trackEvent(ANALYTICS_EVENTS.STEP_COMPLETED, {
    step_number: stepNumber,
    question_id: questionId,
    answer_type: answerType,
    time_on_step_seconds: timeOnStep,
  });
}

/**
 * Track when a user selects an option
 */
export function trackOptionSelected(
  questionId: string,
  optionId: string,
  optionLabel: string
): void {
  trackEvent(ANALYTICS_EVENTS.OPTION_SELECTED, {
    question_id: questionId,
    option_id: optionId,
    option_label: optionLabel,
  });
}

/**
 * Track funnel start
 */
export function trackFunnelStarted(): void {
  trackEvent(ANALYTICS_EVENTS.FUNNEL_STARTED, {
    referrer: document.referrer || 'direct',
    user_agent: navigator.userAgent,
    screen_size: `${window.innerWidth}x${window.innerHeight}`,
  });
}

/**
 * Track funnel completion
 */
export function trackFunnelCompleted(
  state: FunnelState,
  totalTimeSeconds: number
): void {
  trackEvent(ANALYTICS_EVENTS.FUNNEL_COMPLETED, {
    service_type: state.serviceType,
    total_time_seconds: totalTimeSeconds,
    steps_completed: state.currentStep,
    micro_goal: state.answers['micro-goals'],
    security_concern: state.answers['security-concern'],
  });
}

/**
 * Track validation errors
 */
export function trackValidationError(
  questionId: string,
  errorMessage: string
): void {
  trackEvent(ANALYTICS_EVENTS.VALIDATION_ERROR, {
    question_id: questionId,
    error_message: errorMessage,
  });
}

/**
 * Track back button usage
 */
export function trackBackButton(
  fromStep: number,
  toStep: number
): void {
  trackEvent(ANALYTICS_EVENTS.BACK_BUTTON_CLICKED, {
    from_step: fromStep,
    to_step: toStep,
  });
}

/**
 * Track session restoration from localStorage
 */
export function trackSessionRestored(
  stepNumber: number
): void {
  trackEvent(ANALYTICS_EVENTS.SESSION_RESTORED, {
    restored_to_step: stepNumber,
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Calculate time spent since a given timestamp
 * Returns time in seconds
 */
export function calculateTimeSpent(startTime: string): number {
  const start = new Date(startTime).getTime();
  const now = Date.now();
  return Math.round((now - start) / 1000);
}

/**
 * Get device type for analytics
 */
export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
}
